import {
  FAA_SEARCH_RESULTS_PATTERN,
  isInternalDomainUrl,
} from '../../tracking/get-entry-point';

export interface SuburbLink {
  name: string;
  slug: string;
}

const slugToName = (slug: string): string =>
  slug
    .replace(/-[a-z]{2,3}-\d{3,4}$/i, '')
    .split('-')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

// Matches the canonical SRP suburb path, e.g. /real-estate-agents/sydney-nsw-2000/
// (see next.config.js rewrites - this is the real public URL, the
// ?suburbs= query form only exists as an alternate entry point that
// itself gets rewritten to this same destination).
const SRP_SUBURB_PATH_PATTERN =
  /\/real-estate-(?:agents|agencies)\/([a-z0-9-]+-[a-z]{2,3}-\d{4})\/?(?:[?#]|$)/i;

// Same slug shape as SRP_SUBURB_PATH_PATTERN's capture group - the ?suburbs=/
// ?searchParam= query form isn't rewritten by Next, so it has to be validated
// here itself rather than relying on the path pattern above.
const SRP_SUBURB_SLUG_PATTERN = /^[a-z0-9-]+-[a-z]{2,3}-\d{4}$/i;

const getSuburbFromSlug = (
  slug: string | null | undefined,
): SuburbLink | null =>
  slug && SRP_SUBURB_SLUG_PATTERN.test(slug)
    ? { name: slugToName(slug), slug }
    : null;

const getReferrerSrpSuburb = (referrer: string): SuburbLink | null => {
  if (!referrer) {
    return null;
  }

  try {
    const { hostname, pathname, searchParams } = new URL(referrer);

    if (
      !isInternalDomainUrl(hostname) ||
      !FAA_SEARCH_RESULTS_PATTERN.test(pathname)
    ) {
      return null;
    }

    const pathSlug = pathname.match(SRP_SUBURB_PATH_PATTERN)?.[1];
    const queryParamSlug = (
      searchParams.get('searchParam') ||
      searchParams.get('suburbs') ||
      ''
    ).split(',')[0];

    return getSuburbFromSlug(pathSlug) ?? getSuburbFromSlug(queryParamSlug);
  } catch {
    return null;
  }
};

export const getEnquirySuccessSuburb = ({
  searchSuburbSlug,
  referrer,
  agencySuburb,
}: {
  searchSuburbSlug?: string | null;
  referrer: string;
  agencySuburb: SuburbLink | null;
}): SuburbLink | null =>
  getSuburbFromSlug(searchSuburbSlug) ??
  getReferrerSrpSuburb(referrer) ??
  agencySuburb;
